// «Meld fra»-linja: systemets hovedvei, ikke valg nummer fem.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// UX-GJENNOMGANG 2026-08-18 (Veifo/Follo Defence), funn **F2**, prioritet HØY:
//
//   «Feltmelding/PTT — "si det → markør på kartet" — er nøyaktig Delta-følelsen.
//   I dag er den valg nr. 5 i undermenyen «Meldinger». Den burde vært det første
//   og mest synlige verktøyet i hele systemet. Konsekvens: systemets sterkeste
//   kort spilles nesten aldri.»
//
// ⚠ FUNNET ER VERIFISERT I KODEN, IKKE TATT PÅ TRO. `main.ts` hadde
// `{ id: 'ptt', label: 'Feltmelding / PTT' }` som **oppføring 5 av 5** i
// dock-gruppa «Meldinger». Rapporten har bokstavelig rett.
//
// Rapportens ledestjerne, som denne fila er målt mot: *«Bringer dette brukeren
// nærmere kartet, eller lenger fra?»* En linje som alltid står framme, forankret
// i din egen posisjon, med markør på kartet i samme sekund — det er så nært
// kartet man kommer uten å tegne selv.
//
// ── ⚠ ÉN TING RAPPORTEN IKKE KUNNE VITE, OG SOM LØSER TO PROBLEMER ──────────
// Rapportens F6 er «begrepsforvirring»: fire ord for samme verb. Samtidig har vi
// nettopp bygget EKTE push-to-talk (LiveKit-talekanal, `pttvakt.ts`, kort
// PTT-14…19). Navnet «Feltmelding / PTT» på DENNE funksjonen — som ikke overfører
// lyd, men gjør tale om til tekst og tenner en markør — ville derfor blitt
// direkte misvisende i det øyeblikket talekanalen kommer.
//
// Derfor: dette heter **«Meld fra»**, og «PTT» betyr fra nå av bare talekanalen.
// Ett grep som både løfter funksjonen (F2) og rydder begrepet (F6).
//
// ── FASE 2 (2026-08-18): «STRUKTUR ER EN OPPGRADERING, IKKE ET HINDER» ──────
// Rapportens Fase 2 ber om ÉN komposer: «Alt starter likt: et punkt + fri tekst
// eller tale. Trenger du struktur, trykker du en chip — SALUTE, TIC, 9-liner,
// SITREP — og de riktige feltene folder seg ut inni samme melding.»
//
// Linja er derfor ikke lenger bare en sendevei: den er INNGANGEN. Chip-raden over
// feltet oppgraderer det du alt har skrevet til en strukturert melding, og
// `oppgraderFritekst` i contracts avgjør HVOR ordene dine havner — ordrett, i
// meldingstypens fritekstfelt, aldri i et strukturert felt de ikke hører i.
//
// ⚠ CHIPENE VISES BARE NÅR DET STÅR NOE I FELTET. En tom chip-rad ville vært seks
// knapper som gjør det samme som «Ny stridsmelding» alt gjør — altså rapportens
// F1 (samme oppgave, fem verktøy) på nytt, i miniatyr.
import { OPPGRADERINGER, OPPGRADERING_ETIKETT, oppgraderFritekst, type ReportType } from '@skade/contracts';
import { makeRecognizer, type SpeechRec } from './ptt.js';

export interface MeldFraOpts {
  /** Send meldingen. Returner en id vi kan følge leveringen på, eller null. */
  onSend: (tekst: string) => void;
  /**
   * Oppgrader til strukturert melding (Fase 2). Flata åpner komponisten med
   * teksten alt lagt i riktig felt. Utelates → chip-raden bygges ikke.
   */
  onOppgrader?: (type: ReportType, tekst: string) => void;
}

/**
 * Statuslinja under feltet.
 *
 * ⚠ RAPPORTENS F7 ER «UKLAR LEVERINGSSTATUS SVEKKER TILLITEN», og konsekvensen
 * den peker på er presis: *«folk dobbeltmelder på radio for sikkerhets skyld —
 * da har systemet tapt.»* Derfor sier linja hva som skjedde, ikke bare at noe
 * skjedde.
 */
export type MeldStatus = 'klar' | 'lytter' | 'sender' | 'sendt' | 'feil';

export const MELD_STATUS_TEKST: Record<MeldStatus, string> = {
  klar: 'Snakk eller skriv — markøren settes i din posisjon',
  lytter: '🎤 Lytter … snakk nå',
  sender: 'Sender …',
  sendt: '✓ Sendt — markør satt i kartet',
  feil: '⚠ Ikke sendt',
};

/**
 * Tolk en tom eller uslettelig melding.
 *
 * ⚠ EN TOM MELDING SKAL IKKE SENDES, OG BRUKEREN SKAL VITE HVORFOR. Uten dette
 * gir «Meld»-knappen ingen tilbakemelding på et blankt felt, og operatøren tror
 * meldingen gikk.
 */
export function kanMelde(tekst: string): { kan: boolean; grunn: string } {
  const t = tekst.trim();
  if (!t) return { kan: false, grunn: 'Skriv eller si noe først.' };
  if (t.length > 2000) {
    return { kan: false, grunn: 'Meldingen er for lang (maks 2000 tegn). Del den opp.' };
  }
  return { kan: true, grunn: '' };
}

export class MeldFraLinje {
  readonly el: HTMLElement;
  private input!: HTMLTextAreaElement;
  private mic!: HTMLButtonElement;
  private sendKnapp!: HTMLButtonElement;
  private statusEl!: HTMLElement;
  private rec: SpeechRec | null;
  private lytter = false;
  private status: MeldStatus = 'klar';
  private chipRad: HTMLElement | null = null;

  constructor(private opts: MeldFraOpts) {
    this.rec = makeRecognizer();
    this.el = this.bygg();
  }

  /** Gi feltet fokus — dock-knappen peker hit i stedet for å åpne et panel. */
  fokuser(): void {
    this.input.focus();
    this.input.select();
  }

  /** Nåværende status (til test). */
  gjeldendeStatus(): MeldStatus { return this.status; }

  private bygg(): HTMLElement {
    const rad = document.createElement('div');
    rad.className = 'meldfra';

    // Mikrofon først: rapporten peker på tale som den raskeste veien, og venstre
    // plassering gjør den til det første tommelen møter.
    this.mic = document.createElement('button');
    this.mic.type = 'button';
    this.mic.className = 'meldfra-mic';
    this.mic.title = this.rec
      ? 'Snakk meldingen (nb-NO)'
      : 'Talegjenkjenning er ikke tilgjengelig i denne nettleseren — skriv i stedet';
    this.mic.textContent = '🎤';
    // ⚠ DEAKTIVERT MED EN FORKLARING framfor å være en knapp som ikke gjør noe.
    this.mic.disabled = !this.rec;
    this.mic.onclick = () => this.veksleLytting();

    this.input = document.createElement('textarea');
    this.input.className = 'meldfra-input';
    this.input.rows = 1;
    this.input.placeholder = 'Meld fra … (snakk eller skriv → markør på kartet)';
    this.input.onkeydown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); }
    };
    this.input.oninput = () => {
      // Vokser med innholdet, men aldri over tre linjer: linja skal ikke spise
      // kartet den er til for.
      this.input.style.height = 'auto';
      this.input.style.height = `${Math.min(this.input.scrollHeight, 72)}px`;
      const kan = kanMelde(this.input.value).kan;
      this.sendKnapp.disabled = !kan;
      // Chipene dukker opp i det øyeblikket det finnes noe å oppgradere.
      if (this.chipRad) this.chipRad.hidden = !kan;
    };

    this.sendKnapp = document.createElement('button');
    this.sendKnapp.type = 'button';
    this.sendKnapp.className = 'meldfra-send';
    this.sendKnapp.textContent = 'Meld';
    this.sendKnapp.disabled = true;
    this.sendKnapp.onclick = () => this.send();

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'meldfra-status';
    this.statusEl.setAttribute('role', 'status');
    this.statusEl.textContent = MELD_STATUS_TEKST.klar;

    const felt = document.createElement('div');
    felt.className = 'meldfra-rad';
    felt.append(this.mic, this.input, this.sendKnapp);

    // Chip-raden over feltet (Fase 2). Bygges bare når flata faktisk kan
    // oppgradere — ellers ville knappene stått der og ikke gjort noe.
    if (this.opts.onOppgrader) {
      this.chipRad = document.createElement('div');
      this.chipRad.className = 'meldfra-chips';
      this.chipRad.hidden = true;
      for (const t of OPPGRADERINGER) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'meldfra-chip';
        b.textContent = `+ ${OPPGRADERING_ETIKETT[t]}`;
        // Tittelen sier på forhånd HVOR teksten havner. En operatør som først
        // oppdager det etter klikket har alt mistet tilliten til grepet.
        b.title = oppgraderFritekst(t, 'x').forklaring;
        b.onclick = () => this.oppgrader(t);
        this.chipRad.appendChild(b);
      }
      rad.append(this.chipRad);
    }

    rad.append(felt, this.statusEl);
    return rad;
  }

  private settStatus(s: MeldStatus, ekstra = ''): void {
    this.status = s;
    this.statusEl.textContent = ekstra
      ? `${MELD_STATUS_TEKST[s]} — ${ekstra}`
      : MELD_STATUS_TEKST[s];
    this.statusEl.classList.toggle('feil', s === 'feil');
    this.statusEl.classList.toggle('ok', s === 'sendt');
  }

  private veksleLytting(): void {
    if (!this.rec) return;
    if (this.lytter) { this.rec.stop(); return; }
    this.lytter = true;
    this.mic.classList.add('lytter');
    this.settStatus('lytter');
    this.rec.onresult = (e) => {
      const t = e.results?.[0]?.[0]?.transcript ?? '';
      if (t) {
        // Legg til framfor å erstatte: en operatør som har begynt å skrive og så
        // snakker, skal ikke miste det hun skrev.
        this.input.value = this.input.value ? `${this.input.value} ${t}` : t;
        this.input.dispatchEvent(new Event('input'));
      }
    };
    this.rec.onend = () => {
      this.lytter = false;
      this.mic.classList.remove('lytter');
      if (this.status === 'lytter') this.settStatus('klar');
    };
    this.rec.onerror = () => {
      this.lytter = false;
      this.mic.classList.remove('lytter');
      // ⚠ SI AT DET FEILET. En stille mikrofon som ikke hørte noe er umulig å
      // skille fra en mikrofon som hørte stillhet.
      this.settStatus('feil', 'talegjenkjenning feilet — skriv i stedet');
    };
    try { this.rec.start(); } catch {
      this.lytter = false;
      this.mic.classList.remove('lytter');
      this.settStatus('feil', 'kunne ikke starte mikrofonen');
    }
  }

  /**
   * Oppgrader til strukturert melding (Fase 2).
   *
   * ⚠ TØMMER FELTET, men bare etter at teksten er levert videre. Sto den igjen,
   * ville operatøren hatt samme setning to steder og kunnet sende den begge veier
   * — to meldinger om én hendelse er et verre situasjonsbilde enn ingen.
   */
  private oppgrader(type: ReportType): void {
    const sjekk = kanMelde(this.input.value);
    if (!sjekk.kan) { this.settStatus('feil', sjekk.grunn); return; }
    const tekst = this.input.value.trim();
    const o = oppgraderFritekst(type, tekst);
    this.opts.onOppgrader?.(type, tekst);
    this.input.value = '';
    this.input.style.height = 'auto';
    this.sendKnapp.disabled = true;
    if (this.chipRad) this.chipRad.hidden = true;
    // Statuslinja sier hvor ordene ble av — samme forklaring som contracts gir,
    // så flata og kontrakten ikke kan si to ulike ting (§3).
    this.settStatus('klar', o.forklaring);
  }

  private send(): void {
    const sjekk = kanMelde(this.input.value);
    if (!sjekk.kan) { this.settStatus('feil', sjekk.grunn); return; }
    const tekst = this.input.value.trim();
    this.settStatus('sender');
    try {
      this.opts.onSend(tekst);
      this.input.value = '';
      this.input.style.height = 'auto';
      this.sendKnapp.disabled = true;
      this.settStatus('sendt');
      // Etter to sekunder tilbake til «klar» — en permanent «✓ Sendt» ville
      // stått igjen fra forrige melding og lest som kvittering på den neste.
      window.setTimeout(() => { if (this.status === 'sendt') this.settStatus('klar'); }, 2000);
    } catch (e) {
      this.settStatus('feil', e instanceof Error ? e.message : String(e));
    }
  }
}
